import { useMemo } from 'react'
import { ServerExperiments } from '@/experimentation/types'
import { Maybe } from '@/utils/types'
import { ShowtimesVenue } from '../hooks/useVenueSearch/useVenueSearch'
import { isPremierePartnerVenue } from '../utils/premierePartnerUtils'

const getVenuesSortedByPremiereStatus = (
  venues: ShowtimesVenue[],
  preferredVenueID: string,
  theatricalSlug: Maybe<string>,
  experiments: Maybe<ServerExperiments>,
) => {
  // TODO: didn't understand this
  if (!preferredVenueID && !venues.length) return venues

  const venuesWithPremiereStatus = venues.map((venue) => ({
    venue,
    isPremierePartner: isPremierePartnerVenue({
      vendorId: venue.vendorId,
      theaterSlug: theatricalSlug,
      experimentValue: experiments?.['premier-venue-pill-badge'],
    }),
  }))

  return venuesWithPremiereStatus
    .sort((a, b) => {
      // First priority: premium partner venues
      if (a.venue.isPremiumPartner && !b.venue.isPremiumPartner) return -1
      if (!a.venue.isPremiumPartner && b.venue.isPremiumPartner) return 1

      // Second priority: preferred venue
      if (a.venue.providerId === preferredVenueID) return -1
      if (b.venue.providerId === preferredVenueID) return 1

      // Third priority: premiere partner venues
      if (a.isPremierePartner && !b.isPremierePartner) return -1
      if (!a.isPremierePartner && b.isPremierePartner) return 1

      return 0
    })
    .map(({ venue }) => venue)
}

const getVenuesSortedByPreferredVenue = (venues: ShowtimesVenue[], preferredVenueID: string) => {
  if (!preferredVenueID) return venues

  return venues.sort((a, b) => {
    if (a.providerId === preferredVenueID) return -1
    if (b.providerId === preferredVenueID) return 1
    return 0
  })
}

const getVenuesSortedByDistance = (venues: ShowtimesVenue[]) => {
  return venues.sort((a, b) => {
    if (a.distance === undefined) return 1
    if (b.distance === undefined) return -1
    return a.distance - b.distance
  })
}

const useSortedVenues = ({
  venues,
  preferredVenueID,
  theatricalSlug,
  experiments,
}: {
  venues: ShowtimesVenue[]
  preferredVenueID: string
  theatricalSlug: Maybe<string>
  experiments: Maybe<ServerExperiments>
}) => {
  const venuesWithShowtimes = useMemo(() => {
    if (experiments?.['showtime-venues-sort'] === 'distance') {
      const filtered = venues?.filter((venue) => venue?.showtimes.length > 0)
      return getVenuesSortedByDistance(filtered)
    }

    if (experiments?.['showtime-venues-sort'] === 'distance-unlimited') {
      const filtered = venues?.filter((venue) => venue?.showtimes.length > 0 || venue?.isPresaleCompatible)
      return getVenuesSortedByDistance(filtered)
    }

    return getVenuesSortedByPremiereStatus(venues, preferredVenueID, theatricalSlug, experiments)
  }, [venues, preferredVenueID, theatricalSlug, experiments])

  const venuesWithNoShowtimes = useMemo(() => {
    if (experiments?.['showtime-venues-sort'] === 'distance') {
      const filtered = venues?.filter((venue) => venue?.showtimes.length === 0)
      return getVenuesSortedByDistance(filtered)
    }

    if (experiments?.['showtime-venues-sort'] === 'distance-unlimited') {
      const filtered = venues?.filter((venue) => venue?.showtimes.length === 0 && !venue?.isPresaleCompatible)
      return getVenuesSortedByDistance(filtered)
    }

    const filtered = venues?.filter((venue) => venue?.showtimes.length === 0)
    return getVenuesSortedByPreferredVenue(filtered, preferredVenueID)
  }, [venues, preferredVenueID, experiments])

  return { venuesWithShowtimes, venuesWithNoShowtimes }
}

export default useSortedVenues
